import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { useAnnouncementApi } from './config';
import { useAuth } from '../../context/AuthContext';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import OptionsMenu from '../../components/ui/OptionsMenu';

const Container = styled.div``;

export default function AnnouncementsPage() {
  const { t } = useTranslation();
  const { canWrite } = useAuth();
  const history = useHistory();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, ids: [], name: '' });
  const [deleting, setDeleting] = useState(false);

  const { objectSchema, fetching, columns: baseColumns, data, pagination, create, update, _delete } = useAnnouncementApi(queryParams);

  const handleNewButtonClick = useCallback(() => { setSelectedRow(null); setDrawerOpen(true); }, []);
  const handleEdit = useCallback((row) => { setSelectedRow({ ...row }); setDrawerOpen(true); }, []);
  const handleSave = useCallback(async (formData) => {
    const success = selectedRow ? await update(selectedRow.id, formData) : await create(formData);
    if (success) setDrawerOpen(false);
    return success;
  }, [selectedRow, create, update]);

  const handleDelete = useCallback((row) => {
    setDeleteConfirm({ isOpen: true, ids: [row.id], name: row.title });
  }, []);

  const handleRowsDelete = useCallback((ids) => {
    setDeleteConfirm({ isOpen: true, ids, name: `${ids.length} ${t('announcement.pageTitle').toLowerCase()}` });
  }, [t]);

  const handleDeleteConfirm = useCallback(async () => {
    setDeleting(true);
    await _delete(deleteConfirm.ids);
    setDeleteConfirm({ isOpen: false, ids: [], name: '' });
    setDeleting(false);
  }, [_delete, deleteConfirm.ids]);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    setQueryParams(parseTableParams(tableState, baseColumns));
  }, [baseColumns]);

  const handleRowClick = useCallback((row) => {
    if (row?.id) history.push(`/announcements/${row.id}`);
  }, [history]);

  const actionOptions = useMemo(() => {
    const opts = [
      { type: 'details', label: t('common.details'), onClick: (row) => history.push(`/announcements/${row.id}`) },
    ];
    if (canWrite('AN')) {
      opts.push(
        { type: 'divider' },
        { type: 'edit', label: t('common.edit'), onClick: handleEdit },
        { type: 'delete', label: t('common.delete'), onClick: handleDelete },
      );
    }
    return opts;
  }, [t, canWrite, handleEdit, handleDelete, history]);

  const columns = useMemo(() => ([
    ...baseColumns,
    {
      name: 'actions',
      label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (_, tableMeta) => <OptionsMenu actions={actionOptions} row={data[tableMeta.rowIndex]} />,
      },
    },
  ]), [baseColumns, actionOptions, data]);

  return (
    <Container>
      <GenericFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={selectedRow ? t('announcement.edit') : t('announcement.new')}
        schema={objectSchema}
        initialValues={selectedRow}
        onSave={handleSave}
      />

      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title={t('announcement.deleteTitle')}
        message={t('announcement.deleteMessage', { name: deleteConfirm.name })}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteConfirm({ isOpen: false, ids: [], name: '' })}
        loading={deleting}
      />

      <DataTable
        title={t('announcement.pageTitle')}
        fetching={fetching}
        columns={columns}
        data={data}
        totalRowCount={pagination.totalRowCount}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        canCreateNewRecord={canWrite('AN')}
        onNewButtonClick={handleNewButtonClick}
        onRowsDelete={canWrite('AN') ? handleRowsDelete : undefined}
        onRowClick={handleRowClick}
        filterList={filterList}
        onTableChanged={handleDataStateChange}
      />
    </Container>
  );
}
